import { Link } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { SkillSummary } from '@/lib/types'

const STATUSES = ['ALL', 'DRAFT', 'GUARDRAILS_PENDING', 'TESTING', 'CERTIFIED'] as const

export default function SkillsListPage() {
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('ALL')
  const [search, setSearch] = useState('')
  const { data, isLoading, error } = useQuery({
    queryKey: ['skills'],
    queryFn: async () => (await apiClient.get<{ data: SkillSummary[] }>('/api/v1/skills')).data,
  })

  const rows = (data?.data ?? []).filter((s) => {
    if (status !== 'ALL' && s.status !== status) return false
    const q = search.toLowerCase().trim()
    if (!q) return true
    return (
      s.name.toLowerCase().includes(q) ||
      s.description.toLowerCase().includes(q) ||
      s.tags?.some((t) => t.includes(q))
    )
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Skills</h1>
          <p className="text-sm text-neutral-500">{data?.data?.length ?? 0} in workspace</p>
        </div>
        <Link to="/skills/new" className="rounded bg-neutral-900 text-white px-3 py-1.5 text-sm">
          New skill
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          placeholder="Search name, description, tags"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded border px-2 py-1.5 text-sm min-w-[200px]"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}
          className="rounded border px-2 py-1.5 text-sm"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      {isLoading && <p className="text-sm text-neutral-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Failed to load</p>}

      <div className="rounded border bg-white">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500">No skills match.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-neutral-500 border-b">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Domain</th>
                <th className="px-3 py-2">Risk</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Score</th>
                <th className="px-3 py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-b hover:bg-neutral-50">
                  <td className="px-3 py-2">
                    <Link to={`/skills/${s.id}`} className="hover:underline">
                      {s.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{s.domain}</td>
                  <td className="px-3 py-2">{s.riskLevel}</td>
                  <td className="px-3 py-2">{s.status}</td>
                  <td className="px-3 py-2">
                    {s.latestTestScore != null ? `${(s.latestTestScore * 100).toFixed(0)}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-neutral-500">
                    {new Date(s.updatedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
